export const MIN_COLUMNS = 1;
export const MAX_COLUMNS = 12;

export const columnCount = node => {
  const keys = Object.keys(node.slots || {}).filter(key => /^col-\d+$/.test(key));
  return keys.length || Number(node.props?.columns) || 2;
};

/** Only decode proportional CSS; never guess how an arbitrary CSS track behaves. */
export const columnWeights = node => {
  const count = columnCount(node);
  const { template, ratio } = node.props || {};
  if (template?.trim()) {
    const css = template.trim();
    const repeat = /^repeat\(\s*(\d+)\s*,\s*(?:minmax\(\s*0\s*,\s*1fr\s*\)|1fr)\s*\)$/.exec(css);
    if (repeat && Number(repeat[1]) === count) return Array(count).fill(1);
    const tracks = css.replace(/minmax\(\s*0\s*,\s*([\d.]+fr)\s*\)/g, '$1').split(/\s+/);
    const parts = tracks.map(track => /^([\d.]+)(fr|%)$/.exec(track));
    if (parts.length !== count || parts.some(part => !part || !Number.isFinite(Number(part[1])) || !(Number(part[1]) > 0))
      || parts.some(part => part[2] !== parts[0][2])) return null;
    return parts.map(part => Number(part[1]));
  }
  let weights = ratio;
  if (typeof weights === 'string' && weights) {
    try { weights = JSON.parse(weights); } catch (_) { return null; }
  }
  if (weights != null && weights !== '') {
    if (!Array.isArray(weights)) return null;
    if (weights.length) return weights.length === count && weights.every(value => Number.isFinite(Number(value)) && Number(value) > 0) ? weights.map(Number) : null;
  }
  return Array(count).fill(1);
};

export const columnPreset = node => {
  const weights = columnWeights(node);
  if (!weights) return 'custom';
  if (weights.every(value => value === weights[0])) return 'equalColumns';
  if (weights.length === 2 && weights[0] * 7 === weights[1] * 3) return 'sidebarLeft';
  if (weights.length === 2 && weights[0] * 3 === weights[1] * 7) return 'sidebarRight';
  return 'custom';
};

/** Preserve slot identities and descendants. Shrinking occupied columns is a no-op. */
export const resizeColumns = (node, count, layoutWeights) => {
  if (node.type !== 'columns' || !Number.isInteger(count) || count < MIN_COLUMNS || count > MAX_COLUMNS) return null;
  const currentCount = columnCount(node);
  if (layoutWeights !== undefined && (!Array.isArray(layoutWeights) || layoutWeights.length !== count || layoutWeights.some(value => !Number.isFinite(value) || value <= 0))) return null;
  if (count === currentCount && !layoutWeights) return { ...node, props: { ...node.props, columns: count } };
  const slots = node.slots || {};
  if (Object.entries(slots).some(([key, ids]) => !/^col-[1-9]\d*$/.test(key) || !Array.isArray(ids)
    || Number(key.slice(4)) > currentCount || (Number(key.slice(4)) > count && ids.length))) return null;
  const weights = layoutWeights || columnWeights(node);
  if (!weights && count !== currentCount) return null;
  const nextWeights = weights ? weights.slice(0, count) : null;
  if (nextWeights) {
    const average = weights.reduce((sum, value) => sum + value, 0) / weights.length;
    while (nextWeights.length < count) nextWeights.push(Number(average.toFixed(6)) || 1);
  }
  return {
    ...node,
    props: { ...node.props, columns: count, ...(count !== currentCount || layoutWeights ? { template: '', ratio: nextWeights.map(String) } : {}) },
    slots: Object.fromEntries(Array.from({ length: count }, (_, index) => {
      const key = `col-${index + 1}`;
      return [key, [...(slots[key] || [])]];
    })),
  };
};
